import React from "react";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { get, keyBy } from "lodash";
import { flushErrors } from "actions/errorActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { toast } from "react-toastify";
import { payrollConstants } from "actions/types";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";

import { dismissPayroll } from "actions/payrollActions";
import {
  payrollStatusToEnglish,
  payrollStatusToType,
} from "statics/statusToEnglishMapping";

import Alert from "components/Alert";
import Button from "components/Button";
import IconSubheader from "components/IconSubheader";
import IconHeader from "components/IconHeader";
import StatusBox from "components/StatusBox";

import "./DismissPayrollModal.scss";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

const commonDateFormat = "MMM Do YYYY";

import * as yup from "yup";
const schema = yup.object({
  reason: yup.string().required().max(256),
});

interface FormValues {
  reason: string;
}

// payrollData's propType declares arrayOf(object), but the component reads it
// as a single object (props.payrollData.payPeriodId etc.) — type to match real
// usage, not the misleading array propType.
interface PayrollData {
  payPeriodId?: string;
  groupId?: string;
  periodState?: keyof typeof payrollStatusToEnglish;
  payPeriodStart?: string;
  payPeriodEnd?: string;
}

interface OwnProps {
  onClose: () => void;
  onSubmit?: (...args: unknown[]) => void;
  onSuccess: () => void;
  payroll?: unknown[];
  payrollData?: PayrollData;
}

const actions = [payrollConstants.DISMISS_PAYROLL];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const groupsById = keyBy(state.employer.groups, "id");

  return {
    groupsById,
    isFetching: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  dismissPayroll,
  flushErrors,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  reason: string;
}

class DismissPayrollModal extends React.PureComponent<Props, State> {
  // preserved verbatim: original called super() with no args (props not read here)
  constructor() {
    // @ts-expect-error preserve original no-arg super() call; React.Component expects props
    super();

    this.state = {
      reason: "",
    };
  }

  _onClose = () => {
    this.props.flushErrors();
    this.props.onClose();
  };

  _dismissPayroll = (values: FormValues) => {
    const payPeriodId = get(this.props.payrollData, "payPeriodId");

    void this.props
      .dismissPayroll(
        this.props.client as ApolloClient<object>,
        payPeriodId!,
        values.reason
      )
      .then((result) => {
        if (result.success) {
          this.props.onSuccess();
          (toast.success("Successfully dismissed payroll."),
            this.props.onClose());
        } else {
          toast.error("There was a problem dismissing payroll.");
        }
      });
  };

  render() {
    const groupId = get(this.props.payrollData, "groupId");
    const status = get(this.props.payrollData, "periodState");
    const groupName = get(this.props.groupsById[groupId!], "name");
    const start = get(this.props.payrollData, "payPeriodStart");
    const end = get(this.props.payrollData, "payPeriodEnd");
    const period = `${moment(start).format(commonDateFormat)} - ${moment(
      end
    ).format(commonDateFormat)}`;

    return (
      <div>
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={(values) => {
            this._dismissPayroll(values);
          }}
          enableReinitialize={true}
          initialValues={{
            reason: "",
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
          }: FormikProps<FormValues>) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <Modal
                centered
                scrollable
                show={true}
                onHide={this._onClose}
                className="dismissed-payroll"
              >
                <Modal.Header className="dismissed-payroll-header">
                  <div className="payroll-header">
                    <IconHeader
                      variant="cardHeader"
                      headerText={"Dismiss Payroll"}
                    />
                    <IconSubheader subheader="Are you sure you want to dismiss the following payroll?" />
                  </div>
                </Modal.Header>

                <Modal.Body>
                  <Row className="mb-3">
                    <Form.Group className="mb-3">
                      <div className="dismiss-payroll-info" key={groupId}>
                        <span className="payroll-label">
                          <p>Group</p>
                          <p>Payroll Period</p>
                          <p>Status</p>
                        </span>
                        <span>
                          <p>{groupName as React.ReactNode}</p>
                          <p>{period}</p>
                          <span>
                            <StatusBox
                              status={payrollStatusToEnglish[status!]}
                              type={payrollStatusToType[status!]}
                            />
                          </span>
                        </span>
                      </div>
                    </Form.Group>
                  </Row>
                  <div className="break" />
                  <Row className="mb-3">
                    <Form.Group as={Col} md={9}>
                      <Form.Label>Reason for dismissal</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="reason"
                        value={values.reason}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.reason && !!errors.reason}
                        // @ts-expect-error references a non-existent Formik field
                        // `amount` (copy-paste); always undefined, inert.
                        isValid={touched.amount && !errors.amount}
                      />
                      <Form.Control.Feedback type="invalid">
                        Reason is a required field.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  {this.props.error && (
                    <Alert type="error" msg={this.props.error} />
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <div className="dismiss-actions">
                    <span>
                      <Button
                        className="table-btn"
                        color="dismiss"
                        size="sm"
                        btnLabel="Cancel"
                        type="button"
                        name="cancel"
                        onClick={this.props.onClose}
                      />
                    </span>
                    <span style={{ paddingLeft: 3 }}>
                      <Button
                        className="table-btn"
                        color="red"
                        size="sm"
                        btnLabel="Dismiss"
                        name="submit"
                        withArrow={true}
                        loading={this.props.isFetching}
                        // @ts-expect-error preserved verbatim: original passes
                        // form `values` to Formik handleSubmit (expects an
                        // event); coerced/inert at runtime.
                        onClick={() => handleSubmit(values)}
                      />
                    </span>
                  </div>
                </Modal.Footer>
              </Modal>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DismissPayrollModal));
